import { useCallback, useEffect, useMemo, useState } from "react";
import { RootState } from "src/store";
import { useDispatch, useSelector } from "react-redux";
import { ModifierItem } from "src/interfaces/modifier.interface";
import { useModifierItemsByCategoryCorrelationId } from "src/features/ModifierItems/hooks/useModifierItemsByCategoryCorrelationId";
import Spinner from "src/components/common/Spinner";
import ManageModifierItemModal from "src/features/ModifierItems/components/ManageModifierItemModal";
import DeleteItemModal from "src/components/common/DeleteItemModal";
import { useDeleteModifierItem } from "src/features/ModifierItems/hooks/useDeleteModifierItem";
import ModifierItemsTable from "src/features/ModifierItems/components/ModifierItemsTable";
import { MENU_LIST_FILTER_TABS, type MenuListActiveFilter } from "src/constants/menuListActiveFilter";
import { set_modifier_items_list_active_filter } from "src/store/features/modifier-items-slice";

const ModifierItemsPage = () => {
  const [modifierItemToEdit, setModifierItemToEdit] = useState<ModifierItem | null>(null);
  const [isModifierItemModalOpen, setIsModifierItemModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<MenuListActiveFilter>("ACTIVE");
  const dispatch = useDispatch();

  const { modifierItems, toBeDeletedModifierItemId } = useSelector((state: RootState) => state.modifierItems);

  const { isLoadingModifierItems, fetchModifierItemsByCategoryCorrelationId } = useModifierItemsByCategoryCorrelationId();
  const { isDeletingModifierItem, deleteModifierItemMutation, handleCloseDeleteModal, handleOpenDeleteModal } = useDeleteModifierItem();

  const handleAddItem = useCallback(() => {
    setModifierItemToEdit(null);
    setIsModifierItemModalOpen(true);
  }, []);

  const handleEditModifierItem = useCallback((modifierItem: ModifierItem) => {
    setModifierItemToEdit(modifierItem);
    setIsModifierItemModalOpen(true);
  }, []);

  const handleCloseModifierItemModal = useCallback(() => {
    setIsModifierItemModalOpen(false);
  }, []);

  const getFreshModifierItem = useMemo(() => {
    if (modifierItemToEdit?.correlationId) {
      return (
        modifierItems.find((modifierItem) => modifierItem.correlationId === modifierItemToEdit.correlationId) ||
        modifierItemToEdit
      );
    }
    return modifierItemToEdit;
  }, [modifierItemToEdit, modifierItems]);

  useEffect(() => {
    dispatch(set_modifier_items_list_active_filter(activeFilter));
  }, [activeFilter, dispatch]);

  useEffect(() => {
    fetchModifierItemsByCategoryCorrelationId();
  }, [fetchModifierItemsByCategoryCorrelationId]);

  const statusFilterSlot = (
    <div
      className="flex flex-wrap items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white w-full sm:w-fit"
      role="group"
      aria-label="Filter modifier items by status"
    >
      {MENU_LIST_FILTER_TABS.map(({ id, label }) => {
        const selected = activeFilter === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setActiveFilter(id)}
            className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 flex-1 sm:flex-none min-w-0 ${
              selected
                ? "bg-[#FD7657] text-white shadow-sm"
                : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 min-w-0">
      <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 bg-white rounded-3xl border-b border-[#e5e7eb] px-4 py-3 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="w-full min-w-0 sm:flex-1 sm:max-w-none">{statusFilterSlot}</div>
        <div className="flex items-center shrink-0 w-full sm:w-auto justify-end">
          <button
            type="button"
            onClick={handleAddItem}
            className="h-9 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] transition-colors w-full sm:w-auto"
          >
            Add Item
          </button>
        </div>
      </div>

      {isLoadingModifierItems ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : modifierItems.length === 0 ? (
        <p className="text-[#6b7280] py-12 text-center text-sm">No modifier items. Click &quot;Add item&quot; to create one.</p>
      ) : (
        <div className="flex-1 min-h-0 min-w-0 flex flex-col">
          <ModifierItemsTable items={modifierItems} onEdit={handleEditModifierItem} onDelete={handleOpenDeleteModal} />
        </div>
      )}

      <ManageModifierItemModal
        modifierItemToEdit={getFreshModifierItem}
        isModifierItemModalOpen={isModifierItemModalOpen}
        handleCloseModifierItemModal={handleCloseModifierItemModal}
      />

      <DeleteItemModal
        itemType="Item"
        isDeletingItem={isDeletingModifierItem}
        isOpen={!!toBeDeletedModifierItemId}
        onClose={handleCloseDeleteModal}
        onDelete={deleteModifierItemMutation}
      />
    </div>
  );
};

export default ModifierItemsPage;
